/**
 * Panel editor “Páginas del informe”: secciones, iconos, ocultar, orden.
 */
import React, { useMemo, useState } from 'react';
import { ink, studio, SearchField } from '../studioUi';
import {
    NavGlyph,
    PAGE_NAV_ACCENTS,
    PAGE_NAV_ICONS,
    accentHex,
    buildPageOutline,
} from './pageNav';

function dropPlace(event, allowInside) {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientY - rect.top) / Math.max(rect.height, 1);
    if (allowInside && ratio > 0.28 && ratio < 0.72) return 'inside';
    return ratio < 0.5 ? 'before' : 'after';
}

function dropShadow(active, hint) {
    if (hint === 'before') return `inset 0 2px 0 ${ink.accent}`;
    if (hint === 'after') return `inset 0 -2px 0 ${ink.accent}`;
    if (hint === 'inside') return `inset 0 0 0 2px ${ink.accent}`;
    if (active) return `inset 3px 0 0 ${ink.accent}`;
    return 'none';
}

const ReportPageOutline = ({
    open,
    onClose,
    pages = [],
    sections = [],
    activePageId,
    onSelectPage,
    onAddPage,
    onAddSection,
    onRenamePage,
    onRenameSection,
    onPatchPage,
    onPatchSection,
    onMovePage,
    onMoveSection,
    onDeletePage,
    onDeleteSection,
    onDropItem,
}) => {
    const [query, setQuery] = useState('');
    const [editing, setEditing] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [picker, setPicker] = useState(null);
    const [collapsed, setCollapsed] = useState({});
    const [draft, setDraft] = useState({ kind: '', id: '', value: '' });
    const [dragging, setDragging] = useState(null);
    const [dropHint, setDropHint] = useState(null);

    const outline = useMemo(() => buildPageOutline(pages, sections), [pages, sections]);
    const needle = query.trim().toLowerCase();
    const canDrag = !needle;

    const matches = (text) => !needle || String(text || '').toLowerCase().includes(needle);

    const visible = outline.filter((node) => {
        if (!needle) return true;
        if (node.type === 'page') return matches(node.page.name);
        return matches(node.section.name) || node.pages.some((page) => matches(page.name));
    });

    const startRename = (kind, id, value) => {
        setDraft({ kind, id, value });
    };

    const commitRename = () => {
        const name = draft.value.trim();
        if (draft.kind === 'page' && name) onRenamePage(draft.id, name);
        if (draft.kind === 'section' && name) onRenameSection(draft.id, name);
        setDraft({ kind: '', id: '', value: '' });
    };

    const beginDrag = (event, from) => {
        if (!canDrag) return;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', JSON.stringify(from));
        setDragging(from);
    };

    const hintFor = (type, id) => (
        dropHint && dropHint.type === type && dropHint.id === id ? dropHint.place : null
    );

    const overTarget = (event, type, id, allowInside) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (!dragging) return;
        if (dragging.type === type && dragging.id === id) return;
        if (dragging.type === 'page' && type === 'section') {
            setDropHint({ type, id, place: 'inside' });
            return;
        }
        if (dragging.type === 'section' && type === 'page') {
            setDropHint({ type, id, place: dropPlace(event, false) });
            return;
        }
        if (dragging.type === 'section' && type === 'section') {
            setDropHint({ type, id, place: dropPlace(event, false) });
            return;
        }
        setDropHint({ type, id, place: dropPlace(event, allowInside && dragging.type === 'page') });
    };

    const finishDrop = (event, type, id) => {
        event.preventDefault();
        event.stopPropagation();
        const from = dragging || (() => {
            try { return JSON.parse(event.dataTransfer.getData('text/plain') || 'null'); } catch (err) { return null; }
        })();
        const place = (from?.type === 'page' && type === 'section')
            ? 'inside'
            : dropPlace(event, false);
        setDragging(null);
        setDropHint(null);
        if (!from?.id || (from.type === type && from.id === id && place !== 'inside')) return;
        onDropItem?.(from, { type, id, place });
    };

    if (!open) return null;

    return (
        <aside style={ui.panel} aria-label="Páginas del informe">
            <div style={ui.header}>
                <button
                    type="button"
                    style={editing ? ui.iconBtnOn : ui.iconBtn}
                    title="Mostrar u ocultar controles extra"
                    onClick={() => setEditing((value) => !value)}
                >
                    ✎
                </button>
                <strong style={ui.title}>Páginas del informe</strong>
                <button type="button" style={ui.iconBtn} onClick={onClose} title="Cerrar">×</button>
            </div>
            <SearchField value={query} onChange={setQuery} placeholder="Búsqueda" style={{ marginBottom: 0 }} />
            <div style={ui.hint}>
                {canDrag ? 'La sección agrupa hojas: arrastra una o más al recuadro. El lienzo es cada hoja, no la sección.' : 'Limpia la búsqueda para reordenar.'}
            </div>

            <div style={ui.list}>
                {visible.map((node) => {
                    if (node.type === 'page') {
                        return (
                            <PageRow
                                key={node.page.id}
                                page={node.page}
                                active={node.page.id === activePageId}
                                editing={editing}
                                draft={draft}
                                canDrag={canDrag}
                                dropPlaceHint={hintFor('page', node.page.id)}
                                onDragStart={(event) => beginDrag(event, { type: 'page', id: node.page.id })}
                                onDragOver={(event) => overTarget(event, 'page', node.page.id, false)}
                                onDrop={(event) => finishDrop(event, 'page', node.page.id)}
                                onDragEnd={() => { setDragging(null); setDropHint(null); }}
                                onSelect={() => {
                                    setPicker(null);
                                    onSelectPage(node.page.id);
                                }}
                                onStartRename={() => startRename('page', node.page.id, node.page.name)}
                                onDraft={(value) => setDraft((prev) => ({ ...prev, value }))}
                                onCommit={commitRename}
                                onCancel={() => setDraft({ kind: '', id: '', value: '' })}
                                onPatch={onPatchPage}
                                onMove={onMovePage}
                                onDelete={onDeletePage}
                                canDelete={pages.length > 1}
                                picker={picker}
                                setPicker={setPicker}
                                nested={false}
                            />
                        );
                    }
                    const openSection = collapsed[node.section.id] !== true;
                    const sectionActive = node.pages.some((page) => page.id === activePageId);
                    const tone = accentHex(node.section.accent, ink.text);
                    const sectionHint = hintFor('section', node.section.id);
                    return (
                        <div
                            key={node.section.id}
                            style={{
                                ...ui.sectionBlock,
                                backgroundColor: sectionHint === 'inside' ? ink.accentSoft : ink.wash,
                                boxShadow: sectionHint === 'inside' ? `inset 0 0 0 2px ${ink.accent}` : 'none',
                            }}
                            onDragOver={(event) => overTarget(event, 'section', node.section.id, true)}
                            onDrop={(event) => finishDrop(event, 'section', node.section.id)}
                        >
                            <div
                                style={{
                                    ...ui.sectionHead,
                                    backgroundColor: sectionActive ? ink.surface : 'transparent',
                                    color: tone,
                                }}
                            >
                                <span
                                    draggable={canDrag}
                                    onDragStart={(event) => beginDrag(event, { type: 'section', id: node.section.id })}
                                    onDragEnd={() => { setDragging(null); setDropHint(null); }}
                                    style={ui.grip}
                                    title="Arrastrar sección"
                                >
                                    ⠿
                                </span>
                                <button
                                    type="button"
                                    style={ui.caret}
                                    onClick={() => setCollapsed((prev) => ({ ...prev, [node.section.id]: openSection }))}
                                    title={openSection ? 'Contraer' : 'Expandir'}
                                >
                                    {openSection ? '▾' : '▸'}
                                </button>
                                <button
                                    type="button"
                                    style={ui.iconHit}
                                    onClick={() => setPicker(picker?.id === node.section.id ? null : { kind: 'section', id: node.section.id })}
                                    title="Icono y color de la sección"
                                >
                                    <NavGlyph icon={node.section.icon} size={16} color={tone} />
                                </button>
                                {draft.kind === 'section' && draft.id === node.section.id ? (
                                    <input
                                        autoFocus
                                        value={draft.value}
                                        onChange={(e) => setDraft((prev) => ({ ...prev, value: e.target.value }))}
                                        onBlur={commitRename}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                commitRename();
                                            }
                                            if (e.key === 'Escape') setDraft({ kind: '', id: '', value: '' });
                                        }}
                                        style={ui.rename}
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        style={{ ...ui.sectionName, color: tone }}
                                        onClick={() => setCollapsed((prev) => ({ ...prev, [node.section.id]: openSection }))}
                                        onDoubleClick={() => startRename('section', node.section.id, node.section.name)}
                                    >
                                        {node.section.name}
                                    </button>
                                )}
                                {node.section.hidden ? <span style={ui.hiddenMark} title="Oculta en el visor">⊘</span> : null}
                                <div style={ui.rowActions}>
                                    <button type="button" style={ui.tiny} title="Subir sección" onClick={() => onMoveSection?.(node.section.id, -1)}>↑</button>
                                    <button type="button" style={ui.tiny} title="Bajar sección" onClick={() => onMoveSection?.(node.section.id, 1)}>↓</button>
                                    <button type="button" style={ui.tiny} title="Nueva hoja en esta sección" onClick={() => onAddPage(node.section.id)}>+</button>
                                    <button type="button" style={ui.tiny} title={node.section.hidden ? 'Mostrar en el visor' : 'Ocultar en el visor'} onClick={() => onPatchSection(node.section.id, { hidden: !node.section.hidden })}>
                                        {node.section.hidden ? '⊘' : '◉'}
                                    </button>
                                    <button type="button" style={ui.tiny} title="Renombrar sección" onClick={() => startRename('section', node.section.id, node.section.name)}>✎</button>
                                    <button type="button" style={ui.tinyDanger} title="Quitar sección (las hojas se quedan)" onClick={() => onDeleteSection(node.section.id)}>×</button>
                                </div>
                            </div>
                            {picker?.kind === 'section' && picker.id === node.section.id && (
                                <IconAccentPicker
                                    icon={node.section.icon}
                                    accent={node.section.accent}
                                    onIcon={(icon) => onPatchSection(node.section.id, { icon })}
                                    onAccent={(accent) => onPatchSection(node.section.id, { accent })}
                                    onClose={() => setPicker(null)}
                                />
                            )}
                            {openSection && node.pages.filter((page) => matches(page.name) || matches(node.section.name)).map((page) => (
                                <PageRow
                                    key={page.id}
                                    page={page}
                                    active={page.id === activePageId}
                                    editing={editing}
                                    draft={draft}
                                    canDrag={canDrag}
                                    dropPlaceHint={hintFor('page', page.id)}
                                    onDragStart={(event) => beginDrag(event, { type: 'page', id: page.id })}
                                    onDragOver={(event) => overTarget(event, 'page', page.id, false)}
                                    onDrop={(event) => finishDrop(event, 'page', page.id)}
                                    onDragEnd={() => { setDragging(null); setDropHint(null); }}
                                    onSelect={() => {
                                        setPicker(null);
                                        onSelectPage(page.id);
                                    }}
                                    onStartRename={() => startRename('page', page.id, page.name)}
                                    onDraft={(value) => setDraft((prev) => ({ ...prev, value }))}
                                    onCommit={commitRename}
                                    onCancel={() => setDraft({ kind: '', id: '', value: '' })}
                                    onPatch={onPatchPage}
                                    onMove={onMovePage}
                                    onDelete={onDeletePage}
                                    canDelete={pages.length > 1}
                                    picker={picker}
                                    setPicker={setPicker}
                                    nested
                                />
                            ))}
                            {openSection && node.pages.length === 0 && (
                                <div style={ui.emptySlot}>
                                    Sin hojas. Arrastra aquí una o más, o pulsa +.
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div style={ui.fabWrap}>
                {addOpen && (
                    <div style={ui.addMenu}>
                        <button type="button" style={ui.addItem} onClick={() => { onAddPage(); setAddOpen(false); }}>Nueva hoja</button>
                        <button type="button" style={ui.addItem} onClick={() => { onAddSection(); setAddOpen(false); }}>Nueva sección (agrupa hojas)</button>
                    </div>
                )}
                <button
                    type="button"
                    style={ui.fab}
                    title="Añadir hoja o sección"
                    onClick={() => setAddOpen((value) => !value)}
                >
                    +
                </button>
            </div>
        </aside>
    );
};

const PageRow = ({
    page,
    active,
    editing,
    draft,
    onSelect,
    onStartRename,
    onDraft,
    onCommit,
    onCancel,
    onPatch,
    onMove,
    onDelete,
    canDelete,
    picker,
    setPicker,
    nested,
    canDrag,
    dropPlaceHint,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
}) => {
    const tone = accentHex(page.accent, active ? ink.accent : ink.text);
    const renaming = draft.kind === 'page' && draft.id === page.id;
    return (
        <div style={{ ...ui.pageWrap, marginLeft: nested ? 18 : 0 }}>
            <div
                style={{
                    ...ui.pageRow,
                    borderColor: active ? ink.accent : ink.lineStrong,
                    backgroundColor: active ? ink.accentSoft : ink.surface,
                    boxShadow: dropShadow(active, dropPlaceHint),
                }}
                onDragOver={onDragOver}
                onDrop={onDrop}
            >
                <span
                    draggable={Boolean(canDrag && !renaming)}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    style={ui.grip}
                    title="Arrastrar hoja"
                >
                    ⠿
                </span>
                <button
                    type="button"
                    style={ui.iconHit}
                    onClick={() => setPicker(picker?.id === page.id ? null : { kind: 'page', id: page.id })}
                    title="Icono y color de la hoja"
                >
                    <NavGlyph icon={page.icon} size={15} color={tone} />
                </button>
                {renaming ? (
                    <input
                        autoFocus
                        value={draft.value}
                        onChange={(e) => onDraft(e.target.value)}
                        onBlur={onCommit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onCommit();
                            }
                            if (e.key === 'Escape') onCancel();
                        }}
                        style={ui.rename}
                    />
                ) : (
                    <button
                        type="button"
                        style={{ ...ui.pageName, color: tone }}
                        onClick={onSelect}
                        onDoubleClick={onStartRename}
                        title={page.name}
                    >
                        {page.name}
                    </button>
                )}
                {page.hidden ? <span style={ui.hiddenMark} title="Oculta en el visor">⊘</span> : null}
                {editing && (
                    <div style={ui.rowActions}>
                        <button type="button" style={ui.tiny} title="Subir" onClick={() => onMove(page.id, -1)}>↑</button>
                        <button type="button" style={ui.tiny} title="Bajar" onClick={() => onMove(page.id, 1)}>↓</button>
                        <button type="button" style={ui.tiny} title={page.hidden ? 'Mostrar en el visor' : 'Ocultar en el visor'} onClick={() => onPatch(page.id, { hidden: !page.hidden })}>
                            {page.hidden ? '⊘' : '◉'}
                        </button>
                        <button type="button" style={ui.tiny} title="Renombrar" onClick={onStartRename}>✎</button>
                        <button type="button" style={canDelete ? ui.tinyDanger : ui.tiny} disabled={!canDelete} title="Eliminar hoja" onClick={() => onDelete(page.id)}>×</button>
                    </div>
                )}
            </div>
            {picker?.kind === 'page' && picker.id === page.id && (
                <IconAccentPicker
                    icon={page.icon}
                    accent={page.accent}
                    onIcon={(icon) => onPatch(page.id, { icon })}
                    onAccent={(accent) => onPatch(page.id, { accent })}
                    onClose={() => setPicker(null)}
                />
            )}
        </div>
    );
};

const IconAccentPicker = ({ icon, accent, onIcon, onAccent, onClose }) => (
    <div style={ui.picker}>
        <div style={ui.pickerGrid}>
            {PAGE_NAV_ICONS.map((item) => (
                <button
                    key={item.id}
                    type="button"
                    title={item.label}
                    onClick={() => onIcon(item.id)}
                    style={{
                        ...ui.pickerBtn,
                        borderColor: icon === item.id ? ink.accent : ink.line,
                        backgroundColor: icon === item.id ? ink.accentSoft : ink.surface,
                    }}
                >
                    <NavGlyph icon={item.id} size={16} color={ink.text} />
                </button>
            ))}
        </div>
        <div style={ui.swatches}>
            {PAGE_NAV_ACCENTS.map((item) => (
                <button
                    key={item.id || 'default'}
                    type="button"
                    title={item.label}
                    onClick={() => onAccent(item.id)}
                    style={{
                        ...ui.swatch,
                        backgroundColor: item.color,
                        outline: (accent || '') === item.id ? `2px solid ${ink.accent}` : 'none',
                    }}
                />
            ))}
        </div>
        <button type="button" style={studio.ghostBtn} onClick={onClose}>Listo</button>
    </div>
);

const ui = {
    panel: {
        width: 292,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        borderRadius: 12,
        padding: '12px 12px 16px',
        position: 'relative',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    title: {
        flex: 1,
        fontSize: '0.92rem',
        color: ink.text,
    },
    iconBtn: {
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        color: ink.muted,
        cursor: 'pointer',
        fontSize: '1.05rem',
        width: 28,
        height: 28,
        borderRadius: 8,
    },
    iconBtnOn: {
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
        backgroundColor: ink.accentSoft,
        color: ink.accent,
        cursor: 'pointer',
        fontSize: '1.05rem',
        width: 28,
        height: 28,
        borderRadius: 8,
    },
    list: {
        flex: 1,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        paddingRight: 2,
    },
    hint: {
        fontSize: '0.72rem',
        color: ink.muted,
        lineHeight: 1.35,
    },
    grip: {
        cursor: 'grab',
        color: ink.faint,
        fontSize: '0.85rem',
        letterSpacing: '-2px',
        padding: '0 2px',
        userSelect: 'none',
        flexShrink: 0,
    },
    sectionBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '8px 8px 10px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        borderRadius: 12,
    },
    emptySlot: {
        marginLeft: 18,
        padding: '10px 8px',
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: ink.lineStrong,
        borderRadius: 10,
        fontSize: '0.75rem',
        color: ink.muted,
        textAlign: 'center',
        backgroundColor: ink.surface,
    },
    sectionHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        borderRadius: 10,
        padding: '4px 4px 4px 0',
        minHeight: 34,
    },
    caret: {
        borderWidth: 0,
        backgroundColor: 'transparent',
        color: ink.muted,
        cursor: 'pointer',
        width: 20,
        flexShrink: 0,
    },
    iconHit: {
        borderWidth: 0,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        padding: 2,
        display: 'flex',
        alignItems: 'center',
        color: 'inherit',
        flexShrink: 0,
    },
    sectionName: {
        flex: 1,
        minWidth: 0,
        textAlign: 'left',
        borderWidth: 0,
        backgroundColor: 'transparent',
        fontWeight: 800,
        fontSize: '0.72rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    pageWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    pageRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 10,
        padding: '6px 8px',
        minHeight: 36,
    },
    pageName: {
        flex: 1,
        minWidth: 0,
        textAlign: 'left',
        borderWidth: 0,
        backgroundColor: 'transparent',
        fontWeight: 600,
        fontSize: '0.82rem',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    hiddenMark: {
        fontSize: '0.85rem',
        color: ink.faint,
        flexShrink: 0,
    },
    rowActions: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        gap: 2,
    },
    tiny: {
        borderWidth: 0,
        backgroundColor: 'transparent',
        color: ink.accent,
        cursor: 'pointer',
        fontSize: '0.7rem',
        fontWeight: 700,
        padding: '2px 3px',
    },
    tinyDanger: {
        borderWidth: 0,
        backgroundColor: 'transparent',
        color: ink.danger,
        cursor: 'pointer',
        fontSize: '0.9rem',
        padding: '0 3px',
    },
    rename: {
        flex: 1,
        minWidth: 0,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.accent,
        borderRadius: 6,
        padding: '3px 6px',
        fontSize: '0.8rem',
        fontWeight: 700,
    },
    picker: {
        margin: '0 0 4px 18px',
        padding: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 10,
        backgroundColor: ink.wash,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    pickerGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 4,
    },
    pickerBtn: {
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 8,
        height: 30,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
    },
    swatches: {
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
    },
    swatch: {
        width: 16,
        height: 16,
        borderRadius: '50%',
        borderWidth: 0,
        cursor: 'pointer',
        padding: 0,
    },
    fabWrap: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        paddingTop: 4,
    },
    addMenu: {
        position: 'absolute',
        bottom: 48,
        left: 16,
        right: 16,
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        borderRadius: 10,
        boxShadow: '0 10px 28px rgba(28,29,33,0.14)',
        overflow: 'hidden',
        zIndex: 2,
    },
    addItem: {
        width: '100%',
        textAlign: 'left',
        borderWidth: 0,
        backgroundColor: 'transparent',
        padding: '10px 12px',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: '0.85rem',
        color: ink.text,
    },
    fab: {
        width: 44,
        height: 44,
        borderRadius: '50%',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.accent,
        fontSize: '1.6rem',
        lineHeight: 1,
        cursor: 'pointer',
        boxShadow: '0 6px 16px rgba(28,29,33,0.12)',
        fontWeight: 400,
    },
};

export default ReportPageOutline;
